import { useEffect, useState } from 'react'

import { Box, Button, Container, Divider, Link, Modal, Text, TextureCard } from '@doist/marketist'

import { Document } from '@contentful/rich-text-types'
import { DialogDisclosure, useDialogState } from 'ariakit/dialog'
import { Interpolate } from 'components'
import { useTranslation } from 'next-i18next'
import { scrollTo } from 'services'
import { parseRichText } from 'services/contentful'
import { Integration } from 'types/contentful'

import { SCROLL_ANCHOR } from '..'

import ZapierLogo from './zapier.svg'
import GearLogo from './zapier-gear.svg'

import css from './IntegrationAppHeader.module.css'

import type { DialogState } from 'ariakit/dialog'

export function IntegrationAppHeader({ integration }: { integration: Integration }) {
    const { t } = useTranslation('integrations')
    const commonT = useTranslation('common').t
    const dialog = useDialogState()
    const [isMounted, setIsMounted] = useState(false)
    const { name, installUrl, commonContent, howToInstall, zapier } = integration
    const { tagline, learnMoreButton, appLogo } = commonContent
    const howToInstallRichContent = howToInstall?.json as Document | undefined

    useEffect(() => setIsMounted(true), [])

    return (
        <Container>
            <div className={css.headerContainer}>
                <Button
                    className={css.allIntergrationsCTA}
                    href="/integrations#integrations-grid"
                    variant="tertiary"
                    startIcon="ChevronLeft"
                >
                    {t('allIntegrations')}
                </Button>
                <div className={css.logoContainer}>
                    <TextureCard
                        insetBlockStart={{ xs: '16', md: '20' }}
                        insetInlineStart={{ xs: '16', md: '20' }}
                        insetInlineEnd={{ xs: '16', md: '20', lg: '0' }}
                        variant={Math.random() > 0.5 ? 'sunray' : 'teal'}
                    >
                        <div className={css.logoInner}>
                            <img
                                width={80}
                                height={80}
                                src={appLogo.url}
                                alt={appLogo.alt}
                                className={css.integrationLogo}
                            />
                        </div>
                    </TextureCard>
                </div>

                <div>
                    <div className={css.nameContainer}>
                        <Text size="major-lg">
                            {name}

                            {zapier && (
                                <Text tag="small" className={css.via} size="body-sm">
                                    {t('zapier.via')}
                                    <ZapierLogo aria-label="Zapier" />
                                </Text>
                            )}
                        </Text>
                    </div>

                    <Text size="body-base" className={css.tagline}>
                        {tagline}
                    </Text>
                </div>

                {zapier
                    ? ZapierAppButtons({
                          connectBtn: t('zapier.connectBtn'),
                          howToConnectBtn: t('zapier.howToConnectBtn'),
                          installUrl,
                      })
                    : CustomAppButtons({
                          dialog,
                          addBtn: t('add'),
                          moreBtn: t('more'),
                          installUrl,
                          learnMoreButton,
                          howToInstallRichContent,
                      })}

                {zapier ? (
                    <Box tag="section" className={css.zapierTip}>
                        <div className={css.gearIcon}>
                            <GearLogo aria-hidden />
                        </div>
                        <Text className={css.zapierBody} size="body-sm">
                            <Interpolate
                                t={t}
                                i18nKey="zapier.tip"
                                mapping={{
                                    b: <Text size="body-sm" tag="strong" weight="semibold" />,
                                    name,
                                    link: <Link href="http://zapier.com/" target="_blank" />,
                                }}
                            />
                        </Text>
                    </Box>
                ) : null}
            </div>
            <Divider className={css.divider} />

            {isMounted && howToInstallRichContent && (
                <Modal
                    dialog={dialog}
                    title={t('howToInstall', { name })}
                    closeLabel={commonT('close')}
                >
                    {parseRichText(howToInstallRichContent)}
                </Modal>
            )}
        </Container>
    )
}

function CustomAppButtons({
    dialog,
    addBtn,
    moreBtn,
    installUrl,
    howToInstallRichContent,
    learnMoreButton,
}: {
    dialog: DialogState
    addBtn: string
    moreBtn: string
    installUrl: string
    howToInstallRichContent: Document | undefined
    learnMoreButton: string
}) {
    return (
        <div className={css.buttonsContainer}>
            {installUrl ? (
                <Button data-analytics="integration-install" href={installUrl} target="_blank">
                    {addBtn}
                </Button>
            ) : (
                howToInstallRichContent && (
                    <DialogDisclosure as="div" state={dialog}>
                        <Button tabIndex="-1" data-analytics="integration-how-to">
                            {addBtn}
                        </Button>
                    </DialogDisclosure>
                )
            )}
            <Button
                href={learnMoreButton}
                target="_blank"
                data-analytics="integration-learn-more"
                variant="secondary"
            >
                {moreBtn}
            </Button>
        </div>
    )
}

function ZapierAppButtons({
    connectBtn,
    howToConnectBtn,
    installUrl,
}: {
    connectBtn: string
    howToConnectBtn: string
    installUrl: string
}) {
    return (
        <div className={css.buttonsContainer}>
            <Button href={installUrl} target="_blank" data-analytics="integration-install">
                {connectBtn}
            </Button>
            <Button
                onClick={() => scrollTo(SCROLL_ANCHOR)}
                data-analytics="integration-how-to"
                variant="secondary"
            >
                {howToConnectBtn}
            </Button>
        </div>
    )
}
